"use client";

import { createContext, type ReactNode, useContext, useMemo } from "react";

import type {
  FeaturePageCatalog,
  FeaturePageCatalogEntityRef,
  ResolvedFeaturePageEntity,
} from "@garimpa/api/featurePages/types";

interface FeaturePageCatalogIndex {
  getEntity(ref: FeaturePageCatalogEntityRef): ResolvedFeaturePageEntity | undefined;
  getEntityByRef(catalogRefId: string): ResolvedFeaturePageEntity | undefined;
}

const FeaturePageCatalogContext = createContext<FeaturePageCatalogIndex | null>(null);

function entityKey(ref: FeaturePageCatalogEntityRef): string {
  return `${ref.entityType}:${ref.catalogId}`;
}

function createCatalogIndex(catalog: FeaturePageCatalog): FeaturePageCatalogIndex {
  const entitiesByKey = new Map(
    [...catalog.artists, ...catalog.works, ...catalog.releases, ...catalog.companies].map(
      (entity) => [entityKey({ entityType: entity.entityType, catalogId: entity.id }), entity],
    ),
  );

  return {
    getEntity: (ref) => entitiesByKey.get(entityKey(ref)),
    getEntityByRef: (catalogRefId) => {
      const ref = catalog.refs[catalogRefId];
      return ref ? entitiesByKey.get(entityKey(ref)) : undefined;
    },
  };
}

export function FeaturePageCatalogProvider({
  catalog,
  children,
}: {
  catalog: FeaturePageCatalog;
  children: ReactNode;
}) {
  const index = useMemo(() => createCatalogIndex(catalog), [catalog]);
  return (
    <FeaturePageCatalogContext.Provider value={index}>
      {children}
    </FeaturePageCatalogContext.Provider>
  );
}

export function useFeaturePageCatalog(): FeaturePageCatalogIndex {
  const catalog = useContext(FeaturePageCatalogContext);
  if (!catalog) throw new Error("Feature-page catalog is unavailable outside its provider");
  return catalog;
}
